import { useState } from 'react';
import Button from 'src/components/buttons/button';
import BulletedList from 'src/components/bulletedList';
import config from 'config/config';
import Icon from 'src/components/icon';

const PROGRAM_URL = `${config.cdn.navigator}living-with-high-blood-pressure/`;

const getMenu = () => (
  <section className="navigator__contents">
    <div className="navigator__contents-header">Contents</div>
    <ul className="navigator__contents__list">
      <li><div className="navigator__contents__number">1.</div><div className="navigator__contents__content-text"><a href="#1">Reading food labels</a></div></li>
      <li><div className="navigator__contents__number">2.</div><div className="navigator__contents__content-text"><a href="#2">Finding your favourite salt alternatives</a></div></li>
      <li><div className="navigator__contents__number">3.</div><div className="navigator__contents__content-text"><a href="#3">Beat your excuses and start exercising</a></div></li>
      <li><div className="navigator__contents__number">4.</div><div className="navigator__contents__content-text"><a href="#4">Watch: Healthy comfort foods</a></div></li>
    </ul>
  </section>
);

const getPage = () => (
  <div className="navigator">
    <section className="navigator__content">
      <h1 className="navigator__title">
        {'Week 11: Changes for life'}
      </h1>
      <p>
        {`Following a healthy, balanced lifestyle is important for all of us, especially people trying to lower their blood pressure.
        Eating well and being physically active are an important part of that. 
        This week we're thinking about how to make your changes stick, get your blood pressure down and keep it at a healthy level. `}
      </p>
      <div className="visible-xs">{getMenu()}</div>

      <h2>Reading food labels</h2>
      <p>Reading food labels is an important habit to get into if you want to make good decisions every time you are grocery shopping. </p>
      <p>{`With packaged foods, the label is your guide. It's how you can know how many calories are in that food, as well as the amount of salt and sugar.`}</p>
      <p>
        Try to eat mainly green labels with a few ambers, and avoid reds as much as you can.
        The label will also tell you the portion size so you can be careful not to eat too much.
      </p>
      <p>
        {`Remember, it's these small everyday decisions that build up to real changes in your health. Just keep focussed on the choices you can make that day to eat better and improve your blood pressure.`}
      </p>
      <img className="navigator__image-full" src={`${PROGRAM_URL}wk11-label.jpg`} />
      <a href="https://www.bhf.org.uk/heart-matters-magazine/nutrition/sugar-salt-and-fat/10-tips-for-understanding-food-labels" rel="noreferrer" target="_blank">
        Read 10 tips for understanding food labels {'>'}
      </a>

      <h2>Finding your favourite salt alternatives</h2>
      <p>
        {`Cutting back on salt is one of the best things you can do if you need to lower your blood pressure. Eating less salt doesn't have to mean bland and boring food. Here are three delicious ways to season your favourite meals without salt.`}
      </p>
      <p><strong>Herbs</strong>{` - Whether they're fresh or dried, herbs go brilliantly with meats, potatoes and pasta.`}</p>
      <p><strong>Spices</strong>{` - Build up your spice collection to bring your dishes to life. You don't need a huge collection, just a few go-tos. Even if you don't like spicy food, cinnamon, nutmeg and turmeric all add flavour without heat.`}</p>
      <p><strong>Citrus</strong> - For salads, fish and sauces, citrus juice and zest can really add interest to a meal. </p>
      <a href="https://www.bhf.org.uk/heart-matters-magazine/nutrition/herbs-and-spices" rel="noreferrer" target="_blank">
        <Button
          icon="open-right"
          iconLeftAligned={false}
          text="Try these 19 salt alternatives"
        />
      </a>

      <h2>Beat your excuses and start exercising</h2>
      <BulletedList items={[
        `If you don't have time: Build exercise into your day-to-day routine, like cycling to work or taking the stairs instead of the lift. 5 minutes of walking up and down the stairs is better than sitting still.`,
        `If you're stressed: Going for a walk is a much better stress buster than sitting on the sofa watching TV. Just 20 minutes will boost your mood.`,
        `If you don't fancy the gym: You don't have to be in a gym to exercise. Going for a jog, a bike ride or just doing the gardening all count towards your goals.`,
        <span key="1">{`If you can't get out the house - there are loads of free workout videos on the internet, or`}
          <a href="https://www.youtube.com/watch?v=O5YX5xg8Seg&feature=youtu.be" rel="noreferrer" target="_blank"> try this one on the BHF website
          </a>
          .
        </span>]
      }
      />
      <a href="https://www.bhf.org.uk/heart-matters-magazine/wellbeing/ditch-the-excuses" rel="noreferrer" target="_blank">
        Got an excuse? The answer is here {'>'}
      </a>

      <h2>Healthy everyday foods </h2>
      <p>
        {`Whether it's after a long day at work, on a chilly winter's evening or at a family gathering, a nutritious meal is a warming treat. These BHF heart healthy meals will show you how to make small changes to regular dishes so you can enjoy them as you work towards lowering your blood pressure.`}
      </p>
      <div className="navigator__video-container">
        <iframe
          allow="autoplay; encrypted-media; fullscreen"
          src="https://www.youtube.com/embed/SLq8zv4Zpzo "
        />
      </div>
      <a href="https://www.bhf.org.uk/heart-matters-magazine/nutrition/cooking-skills/healthy-comfort-food" rel="noreferrer" target="_blank">
        More healthy, filling meals this way {'>'}
      </a>

      <img alt="Content provided by BHF" className="navigator__image-full navigator__banner-footer" src={`${PROGRAM_URL}bhf-content.jpg`} />
    </section>
  </div>
);

function Footer() {
  const [visible, setVisible] = useState(true);

  if (!visible) return null;
  return (
    <div className="navigator__footer">
      <img alt="HealthUnlocked British Heart Foundation community screenshot" className="navigator__footer__image" src={`${PROGRAM_URL}cta-community.png`} />
      <div className="navigator__footer__inner">
        <div className="navigator__footer__title">
          Join the conversation on HealthUnlocked
        </div>
        <div className="navigator__footer__description">
          {`To get the most out of your heart health journey, you'll need support. Our online community on HealthUnlocked brings patients, their loved-ones and healthcare experts all together online to share advice and encouragement.`}
          {' '}
          <a
            className="navigator__footer__link"
            href="https://healthunlocked.com/bhf"
            rel="noreferrer"
            target="_blank"
          >
            Join the conversation {'>'}
          </a>
        </div>
        <div className="navigator__footer__close" onClick={() => setVisible(false)}>
          <Icon icon="cross" />
        </div>
      </div>
    </div>
  );
}

const obj = {
  getPage,
  getMenu,
  Footer,
  getSeoTitle: 'Living with high blood pressure | HealthUnlocked',
};

export default obj;
